<template>
    <layout>
        <template v-slot:header>
            <AppHead :title="`Inventory Order #${order.order_number || ''}`" />
            <h2>Inventory Order #{{ order.order_number }} Placed on {{ moment(order.date).format('M/D/YY h:mm A') }}</h2>
            <BreadCrumbs activeItem="Inventory Order" :crumbs="{ home: 'Dashboard', 'inventory': 'Inventory' }" />
        </template>

        <template v-slot:default>
            <div class="row mb-3">
                <div class="col-12 col-md-6 col-xl-4 mb-3 mb-md-0">
                    <card title="Order Info" class="h-100">
                        <p class="mb-0 text-secondary">by {{ order.user }}</p>
                        <p class="mb-0">
                            <strong>Manufacturer:</strong> {{ order.manufacturer_name }}
                        </p>
                        <p class="mb-0">
                            <strong>Status:</strong> {{ order.status_desc }}
                        </p>
                    </card>
                </div>

                <div class="col-12 col-md-6 col-xl-4">
                    <card headerStyle="primary" title="Edit PO #" class="h-100">
                        <div class="form-group">
                            <jet-input id="order_number" type="text" class="form-control"
                                v-model="updateForm.order_number" />
                            <jet-input-error :message="form.errors.order_number" class="mt-2" />

                            <jet-button class="w-100 mt-2" @click="edit">Update PO #</jet-button>
                        </div>
                    </card>
                </div>
            </div>

            <div class="table-responsive">
                <table class="table table-striped table-sm tbody-border">
                    <thead>
                        <tr>
                            <th></th>
                            <th>SKU</th>
                            <th>Name</th>
                            <th>Quantity</th>
                            <th class="text-center">Quantity Received</th>
                            <th>Order Cost</th>
                            <th>Cost per Item</th>
                            <th class="text-center">Current Inventory</th>
                            <th class="text-center">Case QTY</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="item in items" :key="item.id">
                            <td>
                                <input type="checkbox" v-bind:value="item.id" @input="selectItem($event, item)"
                                    v-if="!item.from_inventory && item.qty_shipping > 0" />
                            </td>
                            <td class="text-nowrap">{{ item.product_sku }}</td>
                            <td>{{ item.product_name }}</td>
                            <td><input type="text" v-model="item.qty" class="form-control" @blur="updateQty($event, item)"
                                    @keyup.enter="$event.target.blur()" /></td>
                            <td class="text-center">
                                <span>{{ item.received_qty }}</span>
                                <span v-if="item.qty != item.received_qty"> - ETA: {{ item.next_delivered_at_eta ?
                                    moment.utc(item.next_delivered_at_eta).format('M/D/YYYY') : '' }}
                                    <Calendar v-model="item.next_delivered_at_eta_update" :minDate="(new Date())"
                                        :showIcon="true" :selectOtherMonths="true" @date-select="updateETA(item)"
                                        style="max-width: 150px;" />
                                </span>
                            </td>
                            <td>
                                <AmountInput @change="setCost($event, item)" v-model="item.cost_order"
                                    bottomMargin="mb-0" />
                            </td>
                            <td>
                                <AmountInput @change="setItemCost($event, item)" v-model="item.cost_per_item"
                                    bottomMargin="mb-0" />
                            </td>
                            <td class="text-center">{{ item.current_inventory }}</td>
                            <td class="text-center">{{ item.case_qty }}</td>
                        </tr>
                        <tr>
                            <td colspan="3" class="pt-2 font-weight-bold">Totals</td>
                            <td class="pt-2 font-weight-bold">{{ total_qty }}</td>
                            <td class="pt-2 font-weight-bold">{{ total_received_qty }}</td>
                            <td class="pt-2 font-weight-bold"><span class="ml-4">{{ formatDollars(total_cost_order)
                            }}</span></td>
                            <td class="pt-2 font-weight-bold"></td>
                            <td></td>
                            <td></td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <card v-if="shipments.length" headerStyle="dark" title="Shipments" class="mt-3">
                <div class="row">
                    <div class="ml-3 my-2" v-for="shipment in shipments" :key="shipment.id">
                        <card :title="`Received ${shipment.items.length} Items #${shipment.id}`" class="my-2 h-100">
                            <div class="row">
                                <div class="col-4 mb-2">
                                    <p class="text-secondary mb-0">Received {{ shipment.items.length }} Items on {{
                                        moment.utc(shipment.date_received).format('M/D/YY') }}</p>

                                    <p class="mb-0">Status: {{ shipment.status_desc }} <br /></p>

                                    <p class="mb-0" v-if="shipment.invoice_reference">
                                        Invoice #{{ shipment.invoice_reference }}
                                    </p>
                                    <a v-if="shipment.packing_url" :href="shipment.packing_url" target="_new">Packing
                                        Slip</a>
                                </div>
                                <div class="col-2">
                                    <jet-button class="btn btn-danger m-4"
                                        @click="confirmDeleteShipment(shipment)">Delete</jet-button>
                                </div>
                            </div>

                            <div class="table-responsive my-3">
                                <table class="table table-striped tbody-border" style="width: fit-content;">
                                    <thead>
                                        <tr>
                                            <th>Item</th>
                                            <th class="text-center">Cost</th>
                                            <th class="text-center">Qty</th>
                                            <th class="text-center">Subtotal</th>
                                            <th v-if="shipment.shipping_cost > 0" class="text-center">+ Shipping </th>
                                            <th v-if="shipment.discount_amount > 0" class="text-center">- Discount
                                            </th>
                                            <th v-if="shipment.shipping_cost > 0 || shipment.discount_amount > 0"
                                                class="text-right">Total</th>
                                            <th v-if="shipment.shipping_cost > 0 || shipment.discount_amount > 0"
                                                class="text-right">Per Item</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="item in shipment.items" :key="item.id">
                                            <td class="text-nowrap">{{ item.sku }}</td>
                                            <td class="text-right">{{ formatDollars(item.costPerItem) }}</td>
                                            <td>
                                                <input type="text" v-model="item.qty" class="form-control input-group-sm"
                                                    style="max-width: 80px;" @blur="setShipItemQty($event, item, shipment)"
                                                    @keyup.enter="$event.target.blur()" />
                                            </td>
                                            <td>
                                                <AmountInput @change="setShipItemCost($event, item, $shipment)"
                                                    style="max-width: 150px;" v-model="item.itemSubtotal"
                                                    bottomMargin="mb-0" />
                                            </td>
                                            <td v-if="shipment.shipping_cost > 0" class="text-right">
                                                {{ formatDollars(item.shipping) }}
                                            </td>
                                            <td v-if="shipment.discount_amount > 0" class="text-right">
                                                -{{ formatDollars(item.discount) }}
                                            </td>
                                            <td v-if="shipment.shipping_cost > 0 || shipment.discount_amount > 0"
                                                class="text-right">
                                                {{ formatDollars(item.itemTotal) }}
                                            </td>
                                            <td v-if="shipment.shipping_cost > 0 || shipment.discount_amount > 0"
                                                class="text-right">
                                                {{ formatDollars(item.totalCostPerItem) }}
                                            </td>
                                            <td>
                                                <jet-button class="btn btn-sm" btnStyle="danger" icon="fa fa-undo mx-1"
                                                    @click="unreceiveShipmentItem(item)"
                                                    :disable="unreceiveShipmentItemForm.processing">Unreceive</jet-button>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td colspan="3">Totals</td>
                                            <td>
                                                <span class="ml-4"><strong>{{ formatDollars(shipment.shipmentSubtotal)
                                                }}</strong></span>
                                            </td>
                                            <td v-if="shipment.shipping_cost > 0" class="text-right">
                                                {{ formatDollars(shipment.shipping_cost) }}
                                            </td>
                                            <td v-if="shipment.discount_amount > 0" class="text-right">-{{
                                                formatDollars(shipment.discount_amount) }}</td>
                                            <td v-if="shipment.shipping_cost > 0 || shipment.discount_amount > 0"
                                                class="text-right">
                                                <strong>{{ formatDollars(shipment.shipmentTotal) }}</strong>
                                            </td>
                                            <td v-if="shipment.shipping_cost > 0 || shipment.discount_amount > 0">
                                            </td>
                                            <td></td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <div class="mt-2">
                                <div class="col-12 col-md-6 col-xl-4 px-0 mb-2">
                                    <card headerStyle="primary" title="Update">
                                        <label for="date_received_update" class="d-block">Date Received:</label>
                                        <Calendar v-model="shipment.date_received_update" class="w-100"
                                            id="date_received_update" :showIcon="true" :selectOtherMonths="true" />
                                    </card>
                                </div>

                                <div class="form-row">
                                    <div class="form-group col-sm-6">
                                        <jet-label for="invoice_reference" value="Invoice Number / ID" />
                                        <jet-input id="invoice_reference" type="text" class="form-control"
                                            v-model="shipment.invoice_reference" />
                                        <jet-input-error :message="form.errors.invoice_reference" class="mt-2" />
                                    </div>
                                </div>

                                <div class="form-row">
                                    <div class="form-group col-sm-6">
                                        <jet-label for="shipping_cost" value="Shipping Cost" />
                                        <AmountInput inputId="shipping_cost" v-model="shipment.shipping_cost"
                                            @input="onShipmentChange(shipment)" />
                                        <jet-input-error :message="form.errors.shipping_cost" class="mt-2" />
                                    </div>
                                </div>

                                <div class="form-row">
                                    <div class="form-group col-sm-6">
                                        <jet-label for="discount_amount" value="Discount Amount" />
                                        <AmountInput inputId="discount_amount" v-model="shipment.discount_amount"
                                            @input="onShipmentChange(shipment)" />
                                        <jet-input-error :message="form.errors.discount_amount" class="mt-2" />
                                    </div>
                                </div>

                                <div class="form-row">
                                    <div class="form-group col-sm-6">
                                        <label class="font-weight-bold" for="packing_slip_update">Upload New Packing
                                            Slip</label>
                                        <br /><a v-if="shipment.packing_url" :href="shipment.packing_url"
                                            target="_new">Current Packing Slip</a>

                                        <input type="file" class="form-control-file" @change="(e) => updatePackingSlip(e)">
                                    </div>
                                </div>

                                <jet-button :disabled="form.processing" btnStyle="success" icon="fas fa-clock"
                                    class="mr-3 m-w-100 mb-2 mb-lg-0" @click="updateShipment(shipment)">Save</jet-button>
                                <jet-button v-if="shipment.status != 20" icon="fas fa-check" class="m-w-100"
                                    @click="shipmentValidated(shipment)">Save and Mark Validated</jet-button>
                            </div>
                        </card>
                    </div>
                </div>
            </card>

            <div class="row mt-3">
                <div class="col-12 col-lg-6">
                    <card title="Location Info">
                        <div class="form-group">
                            <jet-label for="destination_address_id" value="Destination Location" />
                            <select class="form-control" id="destination_address_id"
                                v-model="updateForm.destination_address_id" @change="saveDestination">
                                <option></option>
                                <option v-for="address in warehouses" :key="address.id" :value="address.id">
                                    {{ address.company_name }} {{ address.address1 }} {{ address.city }}, {{ address.state
                                    }} {{ address.postcode }}
                                </option>
                            </select>
                            <jet-input-error :message="updateForm.errors.destination_address_id" class="mt-2" />
                        </div>

                        <div>
                            <jet-label for="po_notes" value="PO Notes" />
                            <ckeditor id="po_notes" :editor="editor" v-model="updateForm.po_notes" :config="editorConfig">
                            </ckeditor>
                            <jet-input-error :message="updateForm.errors.po_notes" class="mt-2" />
                            <jet-button icon="fas fa-sticky-note" class="mt-2" @click="saveNotes">Save Notes</jet-button>
                        </div>
                    </card>
                </div>

                <div class="col-12 col-lg-6 mt-3 mt-lg-0">
                    <card title="Sends To">
                        <div class="mt-2" v-if="!order.manufacturer_address">
                            <div class="alert alert-danger">No Address set for Manufacturer</div>
                        </div>
                        <div class="mt-2" v-if="!order.destination_address_id">
                            <div class="alert alert-danger">No destination address set</div>
                        </div>
                        <div v-else>
                            <div class="mb-3">
                                <a class="mb-3" :href="route('inventory.viewpo', order)" target="_new"><i
                                        class="text-secondary fas fa-eye mr-1"></i>Preview</a>

                                <jet-input id="test_email" type="text" class="form-control" v-model="form.test_email" />

                                <jet-button icon="fas fa-paper-plane" class="btn-short mr-2 m-w-100 mt-2"
                                    @click="testSend">Send to Test Email</jet-button>
                                <jet-button icon="fas fa-download" class="btn-short mr-2 m-w-100 mt-2"
                                    @click="downloadPO">Download PO pdf</jet-button>
                            </div>

                            <p class="mb-0"><i class="text-secondary fas fa-envelope mr-1"></i>Will be sent to:</p>
                            <ul class="list-unstyled text-secondary">
                                <li v-for="contact in contacts" :key="contact.id" class="ml-3">{{ contact.first_name }} {{
                                    contact.last_name }}
                                    <Email v-model="contact.email" />
                                </li>
                            </ul>
                            <div class="d-flex">

                                <jet-button icon="fas fa-paper-plane" class="btn-short mr-2" @click="send">Send</jet-button>
                                <jet-button icon="fas fa-check" class="btn-short" @click="sent">Mark Sent</jet-button>
                            </div>
                        </div>

                        <hr />
                        <jet-button @click="showAddToInventory">Enter Inventory Received</jet-button>
                        <hr />
                        <jet-button icon="fas fa-check" @click="validated">Mark Complete / Validated</jet-button>
                    </card>
                </div>
            </div>

            <card v-if="adding" title="Enter Inventory Received" class="mt-3">
                <DataTable :value="items" v-if="items.length" sortField="date" :sortOrder="-1" breakpoint="992px"
                    class="p-datatable-sm admin-datatable" v-model:selection="selectedProducts">
                    <template #paginatorLeft>
                        {{ products.length }} Products
                    </template>
                    <Column selectionMode="multiple" headerStyle="width: 3em"></Column>
                    <Column field="name" header="Name" :sortable="true"></Column>
                    <Column field="current_inventory" header="Current Inventory" :sortable="true"></Column>
                    <Column field="sku" header="SKU" :sortable="true"></Column>
                    <Column field="manufacturer_reference" header="Manufacturer Reference" :sortable="true"></Column>
                    <Column field="upc" header="UPC" :sortable="true"></Column>
                </DataTable>

                <card class="mt-3" title="Received Products" v-if="selectedProducts.length">
                    <div class="table-responsive py-2">
                        <table class="table table-striped table-sm">
                            <thead>
                                <tr>
                                    <th class="">Qty</th>
                                    <th class="">Name</th>
                                    <th class="">SKU</th>
                                    <th class="">Manufacturer Reference</th>
                                    <th class="" colspan="2">UPC</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="product in selectedProducts" :key="product.id">
                                    <td>
                                        <input type="text" class="form-control custom-form-inline"
                                            v-model="product.ship_qty" @blur="setQty($event, product)" />
                                    </td>
                                    <td>{{ product.name }}</td>
                                    <td>{{ product.sku }}</td>
                                    <td>{{ product.manufacturer_reference }}</td>
                                    <td>{{ product.upc }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </card>

                <div class="col-12 copl-md-6 col-lg-4 px-0">
                    <card title="Packing Slip" class="mt-3" headerStyle="primary">
                        <div class="form-row mt-3">
                            <div class="form-group col-sm-6">
                                <label class="w-100 font-weight-bold" for="date_received">Date Received:</label>
                                <Calendar v-model="form.date_received" id="date_received" :showIcon="true"
                                    :selectOtherMonths="true" />
                            </div>
                        </div>
                        <div class="form-row">
                            <div class="form-group col-sm-6">
                                <jet-label for="invoice_reference" value="Invoice Number / ID" />
                                <jet-input id="invoice_reference" type="text" class="form-control"
                                    v-model="form.invoice_reference" />
                                <jet-input-error :message="form.errors.invoice_reference" class="mt-2" />
                            </div>
                        </div>

                        <div class="form-row">
                            <div class="form-group col-sm-6">
                                <jet-label for="shipping_cost" value="Shipping Cost" />
                                <jet-input id="shipping_cost" type="text" class="form-control"
                                    v-model="form.shipping_cost" />
                                <jet-input-error :message="form.errors.shipping_cost" class="mt-2" />
                            </div>
                        </div>

                        <div class="form-row">
                            <div class="form-group col-sm-6">
                                <jet-label for="discount_amount" value="Discount Amount" />
                                <jet-input id="discount_amount" type="text" class="form-control"
                                    v-model="form.discount_amount" />
                                <jet-input-error :message="form.errors.discount_amount" class="mt-2" />
                            </div>
                        </div>

                        <div class="form-row">
                            <div class="form-group col-sm-6">
                                <label class="font-weight-bold" for="packing_slip">Packing Slip</label>
                                <input type="file" class="form-control-file" ref="packing_slip" id="packing_slip"
                                    @change="uploadPackingSlip">
                            </div>
                        </div>
                    </card>
                </div>

                <jet-button v-if="selectedProducts.length > 0" @click="addToInventory" icon="fas fa-plus" class="mt-2">Add
                    to Inventory</jet-button>
            </card>
        </template>
    </layout>
</template>

<script>
import Layout from '@/Shared/Admin/Layout'
import FlashMessages from '@/Shared/FlashMessages'

import JetDialogModal from '@/Jetstream/DialogModal'
import JetDangerButton from '@/Jetstream/DangerButton'
import JetInputError from '@/Jetstream/InputError'
import JetLabel from '@/Jetstream/Label'
import JetInput from '@/Jetstream/Input'

import moment from 'moment'
import CKEditor from '@ckeditor/ckeditor5-vue'
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import UploadAdapter from '@/UploadAdapter'
import toastr from 'toastr'
import BreadCrumbs from '@/Shared/BreadCrumbs'
import Calendar from 'primevue/calendar'
import AmountInput from '@/Shared/AmountInput'

export default {
    components: {
        FlashMessages,
        Layout,
        JetInput,
        JetInputError,
        JetLabel,
        JetDialogModal,
        JetDangerButton,
        Calendar,
        BreadCrumbs,
        AmountInput,
        ckeditor: CKEditor.component
    },
    props: {
        order: Object,
        items: Array,
        contacts: Array,
        warehouses: Array,
        shipments: Array,
        total_qty: Number,
        total_received_qty: Number,
        total_cost_order: Number
    },
    data() {
        return {
            adding: false,
            selectedProducts: [],
            productsInventory: [],
            editor: ClassicEditor,
            editorConfig: {
                extraPlugins: [this.uploader],
                product: this.product
            },
            updateForm: this.$inertia.form({
                order_number: this.order.order_number,
                manufacturer_id: this.order.manufacturer_id,
                qty: null,
                cost_order: null,
                cost_per_item: null,
                po_notes: this.order.po_notes,
                destination_address_id: this.order.destination_address_id,
                itemSubtotal: null,
                next_delivered_at_eta: null
            }),
            form: this.$inertia.form({
                date_received: null,
                invoice_reference: null,
                packing_slip: null,
                products: null,
                shipping_cost: null,
                discount_amount: null,
                test_email: null
            }),
            unreceiveShipmentItemForm: this.$inertia.form({
            }),
        }
    },
    created() {
        this.moment = moment;
    },
    methods: {
        setCost(e, item) {
            this.updateForm.cost_order = e.target.value;
            this.updateForm.post(this.route('admin.inventory.updatecost', item));
        },
        updateQty(e, item) {
            this.updateForm.qty = item.qty;
            this.updateForm.post(this.route('admin.inventory.updateitemqty', item), {
                preserveScroll: true
            });
        },
        updateETA(item) {
            this.updateForm.next_delivered_at_eta = item.next_delivered_at_eta_update;
            this.updateForm.post(this.route('admin.inventory.updateitemeta', item), {
                preserveScroll: true
            });
        },
        setItemCost(e, item) {
            this.updateForm.cost_per_item = e.target.value;
            this.updateForm.post(this.route('admin.inventory.updateitemcost', item), {
                preserveScroll: true
            });
        },
        setShipItemCost(e, item, shipment) {
            this.updateForm.itemSubtotal = e.target.value;
            this.updateForm.post(this.route('admin.inventory.updateshipitemcost', item), {
                preserveScroll: true
            });
            this.onShipmentChange(shipment)
        },
        setShipItemQty(e, item, shipment) {
            this.updateForm.qty = e.target.value;
            this.updateForm.post(this.route('admin.inventory.updateshipmentitemqty', item), {
                preserveScroll: true
            });
            this.onShipmentChange(shipment)
        },

        updateShipment(shipment) {
            this.form.date_received = shipment.date_received_update ? shipment.date_received_update : null;
            this.form.invoice_reference = shipment.invoice_reference;
            this.form.shipping_cost = shipment.shipping_cost;
            this.form.discount_amount = shipment.discount_amount;

            this.form.post(this.route('admin.inventory.updateshipment', shipment), {
                preserveScroll: true
            });
        },
        edit() {
            this.updateForm.post(this.route('admin.inventory.update', this.order));
        },
        saveNotes() {
            this.updateForm.post(this.route('admin.inventory.updatenotes', this.order));
        },
        saveDestination() {
            this.updateForm.post(this.route('admin.inventory.updatedestination', this.order));
        },
        downloadPO() {
            axios.post(this.route('admin.inventory.testsendpo', this.order), { download_po_pdf: true }, {
                responseType: 'blob'
            })
                .then(response => {
                    this.downloadResponse(response, 'po.pdf')
                })
        },
        testSend() {
            this.form.post(this.route('admin.inventory.testsendpo', this.order));
        },
        send() {
            this.$inertia.visit(this.route('admin.inventory.sendpo', this.order));

        },
        sent() {
            this.$inertia.visit(this.route('admin.inventory.sent', this.order));

        },
        validated() {
            this.$inertia.visit(this.route('admin.inventory.validated', this.order));

        },
        shipmentValidated(shipment) {
            this.updateShipment(shipment);

            this.$inertia.visit(this.route('admin.inventory.shipmentvalidated', shipment), {
                preserveScroll: true
            });

        },
        showAddToInventory() {
            this.adding = true;
        },
        selected(product) {
            for (var i = 0; i < this.selectedProducts.length; i++) {
                if (this.selectedProducts[i].id == product.id) return i;
            }

            return -1;
        },
        setQty(e, product) {
            this.selectedProducts[this.selected(product)].ship_qty = e.target.value;
        },

        uploadPackingSlip() {
            if (this.$refs.packing_slip) {
                this.form.packing_slip = this.$refs.packing_slip.files[0]
            }
        },
        updatePackingSlip(e) {
            this.form.packing_slip = e.target.files[0]
        },
        addToInventory() {
            if (this.selectedProducts.length == 0) {
                toastr.error("Select products received.");
                return;
            }

            let submitProducts = [];

            for (var i = 0; i < this.selectedProducts.length; i++) {
                submitProducts[i] = {
                    id: this.selectedProducts[i].id,
                    qty: this.selectedProducts[i].ship_qty,
                    cost_per_item: this.selectedProducts[i].cost_per_item
                }
            }

            this.form.products = JSON.stringify(submitProducts);
            this.form.post(this.route('admin.inventory.addToInventory', this.order));
        },
        confirmDeleteShipment(shipment) {
            this.$confirm.require({
                message: 'Are you sure?',
                header: 'Delete Inventory Shipment/Redo Inventory on Products',
                icon: null,
                acceptLabel: 'Delete',
                rejectLabel: 'Close',
                accept: () => {
                    this.$inertia.visit(this.route('admin.inventory.deleteinventory', shipment), {
                        preserveScroll: true
                    })
                },
                reject: () => {
                    //callback to execute when user rejects the action
                }
            });
        },
        uploader(editor) {
            editor.plugins.get('FileRepository').createUploadAdapter = (loader) => {
                return new UploadAdapter(loader, editor.config._config.product);
            };
        },
        unreceiveShipmentItem(item) {
            this.$confirm.require({
                header: 'Confirmation',
                message: 'Are you sure to unreceive the item?',
                icon: null,
                acceptLabel: 'Yes',
                rejectLabel: 'No',
                accept: () => {
                    this.unreceiveShipmentItemForm.post(this.route('admin.inventory.unreceiveInventory', item.id), {
                        preserveScroll: true
                    });
                },
                reject: () => {
                }
            });
        },
        onShipmentChange(shipment) {
            shipment.items.forEach((item, i) => {
                const percent = shipment.shipmentSubtotal == 0 ? 0 : (item.itemSubtotal / shipment.shipmentSubtotal)
                item.costPerItem = item.qty > 0 ? parseFloat(item.itemSubtotal) / parseFloat(item.qty) : 0;
                item.shipping = parseFloat(this.formatCurrency(percent * shipment.shipping_cost))
                item.discount = parseFloat(this.formatCurrency(percent * shipment.discount_amount))

                item.itemTotal = item.itemSubtotal + (item.shipping ?? 0) - (item.discount ?? 0)
                item.totalCostPerItem = item.qty > 0 ? parseFloat(item.itemTotal) / parseFloat(item.qty) : 0;
            })
            this.updateShipmentCostTotal(shipment);
        },
        updateShipmentCostTotal(shipment) {
            shipment.shipmentTotal = parseFloat(shipment.shipmentSubtotal)
                + parseFloat(shipment.shipping_cost ? shipment.shipping_cost : 0)
                - parseFloat(shipment.discount_amount ? shipment.discount_amount : 0)
        },
    },
}
</script>

<style>
.gallery-thumbnail img {
    width: 120px;
}

.table {

    td,
    th {
        vertical-align: middle;
    }
}
</style>
